import { Trash2 } from 'lucide-react';

import { NeedsRepairBadge } from '@/_shared/components/AccountRepair/NeedsRepairBadge';
import { KebabMenu } from '@/_shared/components/list/KebabMenu';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import type { TableColumn } from '@/_shared/components/Table';
import { timeAgo } from '@/_shared/lib/format';

import { audienceRowLabel, isCountStale } from '../lib/audienceList';
import type { AudienceListItem } from '../types';
import { AudienceStatusBadge } from './AudienceStatusBadge';
import { AudienceWarehouseMark } from './AudienceWarehouseMark';
import { CountBadge } from './CountBadge';
import { StaleIndicator } from './StaleIndicator';

interface BuildColumnsOptions {
  onDelete: (id: string) => void;
  /** Per-row gate — the trailing actions cell renders a Delete kebab only for rows this user may delete. */
  canDelete: (item: AudienceListItem) => boolean;
  /**
   * Why `Delete` is unavailable for a row, or `undefined` when it is not: a disabled or gone sibling turns audience
   * actions off, with the banner's text as the reason (ACT2-1124 LLD §5.9), and a synced audience needs the person's
   * "Create and delete audiences" grant there (LLD §6). The item stays in the menu, disabled.
   */
  deleteDisabledReason: (item: AudienceListItem) => string | undefined;
}

/**
 * Columns for the audience list's table view — Audience (with warehouse-type logo), Size,
 * Activations, Updated, and a trailing actions kebab (Delete, per-row gated). Rows are pre-sorted
 * upstream (the toolbar drives one sort for both views), so the columns are pure renderers and the
 * table's own header sorting stays off.
 *
 * ACT2-640 restored two scan facts the row had dropped: the warehouse TYPE logo beside the title
 * (name on hover / accessible name — still searchable via `warehouseLabel`), and an activation count
 * (bare number, hides at zero) so an unconnected audience stays quiet. The conditions count stays on
 * the detail page.
 *
 * The Size sits in its own column as a plain {@link CountBadge}, display-only here — the list can't run a
 * count (that lives on the detail page). Its `counted` / `Unknown` / `Unknown`+error states all read here,
 * so a broken audience is never silent.
 *
 * **{@link StaleIndicator} sits beside the Size**, which is the number it qualifies.
 *
 * The Draft pill stays inline beside the name — so that whether drafts deserve a pill at all stays a
 * product question, and not something this layout change decided by accident.
 */
export function buildAudienceColumns({
  onDelete,
  canDelete,
  deleteDisabledReason,
}: BuildColumnsOptions): TableColumn<AudienceListItem>[] {
  return [
    {
      id: 'title',
      header: 'Audience',
      activatesRow: true,
      activatesRowLabel: audienceRowLabel,
      wrap: true,
      // All-phrasing (`<span>`s) so the cell is valid content inside the Table's row-activator `<button>`.
      cell: (a) => (
        <span className="flex min-w-0 items-center gap-2">
          <AudienceWarehouseMark type={a.warehouseType} warehouseName={a.warehouseLabel} />
          <span title={a.title} className="min-w-0 truncate font-medium text-foreground">
            {a.title}
          </span>
          <AudienceStatusBadge status={a.status} />
          <NeedsRepairBadge listRow state={a.repairState ?? 'ok'} className="shrink-0" />
        </span>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      width: '14rem',
      wrap: true,
      cell: (a) => (
        <span className="flex flex-wrap items-center gap-1.5 text-xs font-normal text-muted-foreground">
          <CountBadge state={a.countState} />
          {isCountStale(a) ? <StaleIndicator /> : null}
        </span>
      ),
    },
    {
      id: 'activations',
      header: 'Activations',
      // Bare number — the header already names the column. Sized so Audience keeps the leftover
      // under `fixedLayout` (Size 14 + this 5 + Updated 9 + actions 3.5).
      width: '5rem',
      // Empty when zero: same quiet zero as AudienceActivationsLink on the detail page. Display-only —
      // a nested link inside the row activator would be a second focusable.
      cell: (a) =>
        a.activationCount > 0 ? (
          <span className="text-xs font-normal text-muted-foreground">{a.activationCount}</span>
        ) : null,
    },
    {
      id: 'activity',
      header: 'Updated',
      // Sized for dayjs's longest relative string, "a few seconds ago", not the "21 hours ago" shape that
      // shows up in every screenshot. The word "Updated" left the cell when it became the header.
      width: '9rem',
      wrap: true,
      cell: (a) => <div className="truncate text-xs font-normal text-muted-foreground">{timeAgo(a.updatedAt)}</div>,
    },
    {
      ...ROW_ACTIONS_COLUMN,
      cell: (a) =>
        canDelete(a) ? (
          <KebabMenu
            label={`Actions for ${a.title}`}
            actions={[
              {
                label: 'Delete',
                icon: Trash2,
                destructive: true,
                disabled: deleteDisabledReason(a) !== undefined,
                disabledReason: deleteDisabledReason(a) ?? '',
                onSelect: () => onDelete(a.id),
              },
            ]}
          />
        ) : null,
    },
  ];
}
